/* eslint-disable no-warning-comments */
import { InvalidateQueryFilters, useQuery, useQueryClient } from '@tanstack/react-query'
import { deleteShow, getShowById, getShows } from 'api/show'
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'

export const useShowsAdmin = (partnerId?: string) => {
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedShowId, setSelectedShowId] = useState<string | null>(null)
    const [isDialogVisible, setIsDialogVisible] = useState(false)
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    const linkToForm = '/admin/musor/form'

    const handleSearch = useCallback(
        (value: string) => {
            setSearchTerm(value)
            queryClient.invalidateQueries(['shows', value] as InvalidateQueryFilters)
        },
        [queryClient],
    )

    const handleEdit = useCallback(
        (id: string) => {
            if (partnerId) {
                navigate(`/admin/musor/form/${id}?partnerId=${partnerId}`)
            } else {
                navigate(`/admin/musor/form/${id}`)
            }
        },
        [navigate, partnerId],
    )

    const handleDelete = useCallback(
        async (id: string) => {
            try {
                await deleteShow(id)
                queryClient.invalidateQueries(['shows'] as InvalidateQueryFilters)
            } catch (error) {
                console.error('Error deleting show:', error)
            }
        },
        [queryClient],
    )

    const handleOpenDialog = useCallback(() => {
        setIsDialogVisible(true)
    }, [])

    const handleCloseDialog = useCallback(() => {
        setSelectedShowId(null)
        setIsDialogVisible(false)
    }, [])

    const tableConfig = {
        titleRow: {
            title: 'Műsor',
            buttonLabel: 'Műsor felvétele',
            formConfig: {
                dialogProps: {
                    header: 'Műsor felvétele',
                },
            },
        },
        headers: [],
        idName: 'show_id',
    }

    const {
        data: showsData,
        isLoading: showsLoading,
        error: showsError,
    } = useQuery({
        queryKey: ['shows', searchTerm],
        queryFn: () => getShows(searchTerm, true, partnerId),
    })

    const {
        data: selectedShowData,
        isLoading: selectedShowLoading,
        error: selectedShowError,
    } = useQuery({
        queryKey: ['show', selectedShowId],
        queryFn: () => (selectedShowId ? getShowById(selectedShowId) : Promise.resolve(null)),
        enabled: !!selectedShowId,
    })

    // TODO: dynamic orgid of user instead 0 as default

    const selectedShow = selectedShowData
        ? {
              ...selectedShowData,
              org_id: selectedShowData.org_id ?? 0,
              show_id: selectedShowData.show_id ?? undefined,
          }
        : null

    return {
        showsData,
        showsLoading,
        showsError,
        selectedShowData: selectedShow,
        selectedShowLoading,
        selectedShowError,
        linkToForm,
        tableConfig,
        handleEdit,
        handleDelete,
        handleSearch,
        handleOpenDialog,
        handleCloseDialog,
        isDialogVisible,
    }
}
